// services/leads-details-service.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { BaseApiService } from './base-api.service';
import {
  LeadInfo,
  LeadHistoricoSimulacoes
} from '../interfaces/leads-info-history.interface';
import { HttpParams } from '@angular/common/http';
import { LocalStorageUtil } from '../utils/local-storage.util';
import { SelectedDnService } from './selected-dn.service';

// Interfaces locais do serviço
export interface ContactHistory {
  Data: string;
  Vendedor: string;
  Campanha: string;
  Observação: string;
  Status: string;
  'Retornar Em': string;
}

export interface ApiContactHistory {
  contactDate: string;     // era dataContato
  seller: string;          // era vendedor
  campaign: string;        // era campanha
  observation: string;     // era observacao
  statusTo: string;        // mantém igual
  returnDate: string;      // era retornarEm
}

@Injectable({
  providedIn: 'root'
})
export class LeadsDetailsService extends BaseApiService {

  // Base URL específica para as APIs do banco (configurável por environment / Parameter Store)
  private get bancoApiBaseUrl(): string {
    return this.configService.getRequiredByPath<string>('bancoVw.apiBaseUrl');
  }

  constructor(
    http: HttpClient,
    private selectedDnService: SelectedDnService,
    // ...existing code...
  ) {
    super(http);
  }

  // Funções existentes
  getLeadDetails(leadId: string): Observable<any> {
    const dn = this.selectedDnService.getSelectedDn();
    if (!dn) {
      return throwError(() => new Error('DN não selecionado'));
    }
    const endpoint = `/lead/${dn}/${leadId}/detalhes`;
    return this.get<any>(endpoint);
  }

  getContactHistory(leadId: string): Observable<ContactHistory[]> {
    const dn = this.selectedDnService.getSelectedDn();
    if (!dn) {
      return throwError(() => new Error('DN não selecionado'));
    }
    const endpoint = `/lead/${dn}/${leadId}/historico/contatos`;
    return this.get<ApiContactHistory[]>(endpoint).pipe(
      map(apiData => {
        const sortedData = [...apiData].sort((a, b) =>
          new Date(b.contactDate).getTime() - new Date(a.contactDate).getTime()
        );
        return this.mapApiDataToContactHistory(sortedData);
      }),
      catchError(error => {
        if (error.status === 404) {
          return throwError(() => new Error('LEAD_NOT_FOUND'));
        } else if (error.status === 403) {
          return throwError(() => new Error('FORBIDDEN'));
        } else {
          return throwError(() => new Error('GENERIC_ERROR'));
        }
      })
    );
  }

  // ATUALIZADO: Recebe o token como parâmetro
  getLeadsInfo(token: string, cpfCnpj: string, codigoConcessionaria: string): Observable<LeadInfo> {

    // Remova espaços dos parâmetros
    const cleanCpfCnpj = cpfCnpj.trim();
    const cleanConcessionaria = codigoConcessionaria.trim();

    // URL sem query params
    const leadInfoPathTemplate = this.configService.getRequiredByPath<string>('bancoVwUrlParts.leadInfoPath');
    const leadInfoPath = leadInfoPathTemplate.replace('{cpfCnpj}', cleanCpfCnpj);
    const url = `${this.bancoApiBaseUrl}${leadInfoPath}`;

    // Use HttpParams para construir os parâmetros corretamente
    const params = new HttpParams()
      .set('CodigoMontadora', '0')
      .set('CodigoConcessionaria', cleanConcessionaria)
      .set('ServidorProposta', 'CcoSvcWeb01');

    if (this.configService.isDebugEnabled()) {
      console.log('🔗 URL final:', url);
      console.log('📋 Parâmetros:', params.toString());
      console.log('🧹 codigoConcessionaria limpo:', `"${cleanConcessionaria}"`);
    }

    return this.getHttpClient().get<LeadInfo>(url, {
      headers: this.getBancoHeaders(token),
      params: params // Angular irá encodar corretamente
    }).pipe(
      catchError(error => {


        console.error('Erro ao buscar informações do lead:', error);
        if (error.status === 404) {
          return throwError(() => new Error('LEAD_NOT_FOUND'));
        } else if (error.status === 401 || error.status === 403) {
          return throwError(() => new Error('UNAUTHORIZED'));
        }
        return throwError(() => new Error('GENERIC_ERROR'));
      })
    );
  }

  getLeadsHistoricoSimulacoes(
    token: string, // ADICIONE ESTE PARÂMETRO
    idLeads: number,
    codigoConcessionaria: string
  ): Observable<LeadHistoricoSimulacoes> {
    const historicoPathTemplate = this.configService.getRequiredByPath<string>('bancoVwUrlParts.historicoSimulacoesPath');
    const historicoTemplate = historicoPathTemplate
      .replace('{idLeads}', String(idLeads))
      .replace('{codigoConcessionaria}', codigoConcessionaria);
    const url = `${this.bancoApiBaseUrl}${historicoTemplate}`;

    return this.getHttpClient().get<LeadHistoricoSimulacoes>(url, {
      headers: this.getBancoHeaders(token) // Passa o token aqui
    }).pipe(
      catchError(error => {
        console.error('Erro ao buscar histórico de simulações:', error);
        if (error.status === 401 || error.status === 403) {
          return throwError(() => new Error('UNAUTHORIZED'));
        }
        return throwError(() => new Error('GENERIC_ERROR'));
      })
    );
  }

  // ATUALIZADO: Função combinada com token
  getLeadCompleto(token: string, cpfCnpj: string, codigoConcessionaria: string): Observable<{
    leadInfo: LeadInfo;
    historicoSimulacoes: LeadHistoricoSimulacoes;
  }> {
    return new Observable(observer => {
      this.getLeadsInfo(token, cpfCnpj, codigoConcessionaria).subscribe({
        next: (leadInfo) => {
          this.getLeadsHistoricoSimulacoes(token, leadInfo.IdLeads, codigoConcessionaria).subscribe({
            next: (historico) => {
              observer.next({
                leadInfo,
                historicoSimulacoes: historico
              });
              observer.complete();
            },
            error: (err) => observer.error(err)
          });
        },
        error: (err) => observer.error(err)
      });
    });
  }

  // ATUALIZADO: Recebe o token como parâmetro
  private getBancoHeaders(token: string) {
    return {
      'Authorization': `Bearer ${token}`, // Usa o token passado
      'Content-Type': 'application/json',
    };
  }

  // Funções auxiliares privadas
  private mapApiDataToContactHistory(apiData: ApiContactHistory[]): ContactHistory[] {
    return apiData.map(item => ({
      'Data': this.formatDate(item.contactDate),
      'Vendedor': item.seller,
      'Campanha': item.campaign,
      'Observação': item.observation,
      'Status': item.statusTo,
      'Retornar Em': item.returnDate ? this.formatDate(item.returnDate) : '-'
    }));
  }

  private formatDate(dateString: string): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return dateString;
    }
  }

  getSimulacaoDetails(
    idSimulacao: number
  ): Observable<any> {    
    const token = LocalStorageUtil.getAccessToken();
    const simulacaoPathTemplate = this.configService.getRequiredByPath<string>('bancoVwUrlParts.simulacaoPath');
    const simulacaoPath = simulacaoPathTemplate.replace('{idSimulacao}', String(idSimulacao));
    const url = `${this.bancoApiBaseUrl}${simulacaoPath}`;
    //console.log('Token usado na requisição:', token);
    return this.getHttpClient().get<any>(url, {
      headers: this.getBancoHeaders(token)
    }).pipe(
      catchError(error => {
        console.error('Erro ao buscar detalhes da simulação:', error);
        if (error.status === 401 || error.status === 403) {
          return throwError(() => new Error('UNAUTHORIZED'));
        }
        return throwError(() => new Error('GENERIC_ERROR'));
      })
    );
  }
}


